import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiBarChart2,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiSettings,
  FiTrendingUp,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";

import Footer from "../../../components/Footer/Footer";
import Navbar from "../../../components/Navbar/Navbar";
import { isMasterUser } from "../../../constants/authorization";
import { obterDadosUsuario2, obterPerfilUsuario } from "../../../services/cookies";
import "./HomeGestor.css";

type GestorAction = {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  accent: "amber" | "teal" | "blue";
};

type GestorSection = {
  label: string;
  title: string;
  description: string;
  actions: GestorAction[];
};

const gestorSections: GestorSection[] = [
  {
    label: "Clientes",
    title: "Cadastros e relacionamento",
    description: "Acompanhe perfis, documentos e acessos da base Omex Capital.",
    actions: [
      {
        title: "Gerenciar cadastro",
        description: "Aprovar, revisar ou reprovar cadastros com documentos enviados.",
        href: "/gerenciar/cadastro",
        icon: <FiUserCheck />,
        accent: "amber",
      },
      {
        title: "Todos os clientes",
        description: "Consultar a lista de clientes, dados operacionais e dashboards.",
        href: "/usuarios",
        icon: <FiUsers />,
        accent: "teal",
      },
    ],
  },
  {
    label: "Financeiro",
    title: "Operações financeiras",
    description: "Valide movimentações, execute fechamento e consulte o histórico.",
    actions: [
      {
        title: "Validação de aporte",
        description: "Conferir depósitos e recibos antes da liberação operacional.",
        href: "/validacao/aporte",
        icon: <FiCheckCircle />,
        accent: "amber",
      },
      {
        title: "Solicitação de saque",
        description: "Validar pedidos de retirada e registrar comprovantes.",
        href: "/solicitacoes/saque",
        icon: <FiDollarSign />,
        accent: "blue",
      },
      {
        title: "Fechamento assessoria",
        description: "Executar o fechamento diário das carteiras de assessoria.",
        href: "/fechamento",
        icon: <FiBriefcase />,
        accent: "teal",
      },
      {
        title: "Fechamento private",
        description: "Acessar a seleção de clientes para fechamento private.",
        href: "/funcionario/clientes-private",
        icon: <FiTrendingUp />,
        accent: "amber",
      },
      {
        title: "Histórico de fechamento",
        description: "Consultar períodos anteriores e auditoria de fechamentos.",
        href: "/gerenciar/historico-fechamento",
        icon: <FiClock />,
        accent: "blue",
      },
    ],
  },
  {
    label: "Comando",
    title: "Painel de controle operacional",
    description: "Gerencie comunicações, cartas mensais, informes fiscais e extratos de clientes.",
    actions: [
      {
        title: "Painel de Comando",
        description: "Centralizar avisos, relatórios e geração de documentos em PDF.",
        href: "/gerenciar/painel-controle",
        icon: <FiSettings />,
        accent: "amber",
      },
      {
        title: "Dashboards",
        description: "Abrir painéis gerenciais de acompanhamento da operação.",
        href: "/gerenciar/dashboards",
        icon: <FiBarChart2 />,
        accent: "teal",
      },
    ],
  },
];

const flowSteps = [
  "Cadastro",
  "Validação",
  "Movimentação",
  "Fechamento",
  "Histórico",
];

const HomeGestor = () => {
  const hasMasterAccess =
    obterPerfilUsuario() === "Administrador" &&
    isMasterUser(obterDadosUsuario2().email);
  const visibleSections = gestorSections.map((section) => ({
    ...section,
    actions: section.actions.filter(
      (action) => hasMasterAccess || action.href !== "/gerenciar/cadastro"
    ),
  }));
  const totalActions = visibleSections.reduce(
    (total, section) => total + section.actions.length,
    0
  );

  return (
    <>
      <Navbar />

      <main className="gestor-home-page">
        <section className="gestor-home-hero">
          <div className="gestor-home-hero-copy">
            <span className="gestor-home-eyebrow">Gestão Omex Capital</span>
            <h1>Painel do gestor</h1>
            <p>
              Centralize cadastros, movimentações financeiras e rotinas internas
              com uma experiência mais direta para a operação.
            </p>
          </div>

          <div className="gestor-home-command-panel" aria-label="Resumo operacional">
            <div>
              <span>Rotinas disponíveis</span>
              <strong>{totalActions}</strong>
            </div>
            <div>
              <span>Frentes de trabalho</span>
              <strong>{visibleSections.length}</strong>
            </div>
            <div>
              <span>Ambiente</span>
              <strong>Seguro</strong>
            </div>
          </div>
        </section>

        <section className="gestor-home-flow" aria-label="Fluxo operacional">
          {flowSteps.map((step, index) => (
            <React.Fragment key={step}>
              <div className="gestor-home-flow-step">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{step}</strong>
              </div>
              {index < flowSteps.length - 1 && <FiArrowRight aria-hidden="true" />}
            </React.Fragment>
          ))}
        </section>

        <div className="gestor-home-sections">
          {visibleSections.map((section) => (
            <section className="gestor-home-section" key={section.title}>
              <div className="gestor-home-section-header">
                <span>{section.label}</span>
                <h2>{section.title}</h2>
                <p>{section.description}</p>
              </div>

              <div className="gestor-home-action-grid">
                {section.actions.map((action) => (
                  <Link
                    className={`gestor-home-action-card ${action.accent}`}
                    to={action.href}
                    key={action.title}
                  >
                    <span className="gestor-home-action-icon">{action.icon}</span>
                    <span className="gestor-home-action-copy">
                      <strong>{action.title}</strong>
                      <small>{action.description}</small>
                    </span>
                    <FiArrowRight className="gestor-home-action-arrow" aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default HomeGestor;
